import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ordererMethods } from "../api/apiMethods";
import useSelectedMunicipalityId from "../hooks/useSelectedMunicipalityId";
import { queryKeys } from "../queries/queryKeys";
import { Logger } from "../utils/logger/Logger";
import { ChangeOrdererRoleInput } from "../views/OrdererView/ordererTypes";

export const useChangeOrdererRoleMutation = (options: {
    onSuccess: () => void;
    onError: (error: any) => void;
}) => {
    const queryClient = useQueryClient();
    const municipalityId = useSelectedMunicipalityId();

    const mutation = useMutation({
        mutationFn: async (data: { ordererId?: string; input: ChangeOrdererRoleInput }) => {
            if (!municipalityId) {
                throw new Error("No municipality Id");
            }

            if (!data.ordererId) {
                throw new Error("No orderer id");
            }

            return ordererMethods(municipalityId).changeOrdererRole(
                data.ordererId,
                data.input,
            );
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.orderer.byId(municipalityId, variables.ordererId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.administrator.byId(municipalityId, variables.ordererId),
            });
            options.onSuccess();
        },
        onError: async (error: Response) => {
            Logger.error(error);

            let parsedError: Record<string, unknown> | undefined;
            try {
                parsedError = await error.json();
            } catch {}

            options.onError(
                parsedError ? { ...parsedError, httpStatus: error.status } : error,
            );
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.orderer.all(municipalityId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.administrator.all(municipalityId),
            });
        },
    });

    return mutation;
};
